import { useEffect, useState } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import type { User } from 'firebase/auth';
import { db } from '../firebase';
import type { VisibleAppsContext } from '@/vendor/elevia-ui/react';

/**
 * App picker ("Switch workspace") context. Navigation only: it decides which Elevia app tiles the
 * picker shows, never what the user may do (each app and the Firestore rules decide that).
 *
 * It reads the same inputs Cultivation uses: the `roleId` custom claim (synced from team/ by
 * onTeamWriteSyncClaims) and roles/{roleId}.permissions. The kit registry's tables map those
 * permissions to workspaces and home pages. Until the role is known, or when it cannot be read,
 * the picker shows only the Controller tile.
 */
export interface PickerTables {
  workspacePermissions: Record<string, readonly string[]>;
  homePagePermissions: Record<string, readonly string[]>;
}

export const CONTROLLER_ONLY_CTX: VisibleAppsContext = {
  isAdmin: false,
  workspaces: new Set(['CONTROLLER']),
  permissions: new Set(),
  hasHomePage: () => false,
  controllerAccess: true,
  sharedDevice: false,
  availabilityOnly: false,
};

/** Cultivation's admin role id: admins hold every permission there. */
const LAB_ADMIN_ROLE_ID = 'admin';

export function buildPickerContext(
  tables: PickerTables,
  roleId: string,
  permissions: readonly string[],
  sharedDevice = false,
): VisibleAppsContext {
  const isAdmin = roleId === LAB_ADMIN_ROLE_ID;
  const held = new Set(permissions);
  const holdsAny = (keys: readonly string[] | undefined) =>
    isAdmin || (keys ?? []).some(key => held.has(key));
  const workspaces = new Set(['CONTROLLER']);
  for (const [workspace, keys] of Object.entries(tables.workspacePermissions)) {
    if (holdsAny(keys)) workspaces.add(workspace);
  }
  return {
    isAdmin,
    workspaces,
    permissions: held,
    hasHomePage: workspace => holdsAny(tables.homePagePermissions[workspace]),
    controllerAccess: true,
    sharedDevice,
    availabilityOnly: false,
  };
}

export function useLabPickerContext(user: User | null, tables: PickerTables): VisibleAppsContext {
  const [ctx, setCtx] = useState<VisibleAppsContext>(CONTROLLER_ONLY_CTX);

  useEffect(() => {
    setCtx(CONTROLLER_ONLY_CTX);
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        const { claims } = await user.getIdTokenResult();
        const roleId = claims.roleId;
        if (typeof roleId !== 'string' || !roleId) return;
        const role = await getDoc(doc(db, 'roles', roleId));
        const raw = role.exists() ? role.data().permissions : undefined;
        const permissions = Array.isArray(raw)
          ? raw.filter((key): key is string => typeof key === 'string')
          : [];
        if (!cancelled) {
          setCtx(buildPickerContext(tables, roleId, permissions, Boolean(claims.pinSessionId)));
        }
      } catch {
        // Keep the Controller-only picker; the link to Cultivation's picker stays.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, tables]);

  return ctx;
}
